import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import { FormContext } from 'contexts/FormContext';
import { LmsContext, AccreditationConstants } from 'contexts/LmsContext';

import {
  StepContainer,
  FormNavigation,
  ControlInputGroup
} from 'components/CASAForm';
import {
  AuCallout
} from 'components/DTA';

import { DroneAccreditationFormData, DroneAccreditationAnswer } from 'types/forms/droneAccreditationTypes';
import { IFormAlertType } from 'types/forms/formTypes';

interface IAssessmentQuestionProps {
  currentQuestionIndex: number;
  setCurrentQuestionIndex: (index: number) => void;
  submitLmsForm: (values: DroneAccreditationFormData) => Promise<void>;
}

const AssessmentQuestion: React.FC<IAssessmentQuestionProps> = ({ currentQuestionIndex, setCurrentQuestionIndex, submitLmsForm }) => {
  const { values, setFieldValue, validateForm } = useFormikContext<DroneAccreditationFormData>();
  const { openStepById, setFormAlert, handleBack } = useContext(FormContext);
  const { questions, checkAnswers } = useContext(LmsContext);

  const [isAnswered, setIsAnswered] = useState<boolean>(false);
  const [isSelection, setIsSelection] = useState<boolean>(false);

  useEffect(() => {
    // Set default values if one doesn't exist
    setIsAnswered(false);
    if (currentQuestionIndex !== null && !values.answers[currentQuestionIndex]) {
      setFieldValue(`answers[${currentQuestionIndex}]`, {
        questionId: questions[currentQuestionIndex].id,
        answerIds: []
      } as DroneAccreditationAnswer);
    }
    else {
      if (values.answers[currentQuestionIndex].answerIds.length > 0) {
        setIsAnswered(true);
      }
    }
  }, [currentQuestionIndex]);

  useEffect(() => {
    // Reset current question on first load
    setCurrentQuestionIndex(0);
  }, []);

  useEffect(() => {
    setIsSelection(false);
    if (values.answers[currentQuestionIndex]?.answerIds.length > 0) {
      setIsSelection(true);
    }
  }, [values.answers[currentQuestionIndex]?.answerIds]);

  const customHandleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
    else {
      handleBack();
    }
  };

  const handleNext = async () => {
    setFormAlert(null);
    // Run form validation to make sure questions are answered
    await validateForm();

    // Don't proceed to next question if answer not provided
    const answers = get(values, `answers[${currentQuestionIndex}].answerIds`);

    if (!answers || answers.length <= 0) {
      const alert: IFormAlertType = {
        as: 'error',
        title: 'There was a problem with the form',
        message: 'Please check the form below and try again'
      };
      setFormAlert(alert);
      window.scrollTo(0, 0);
      return;
    }

    if (isAnswered) {
      if (currentQuestionIndex < questions.length - 1) {
        // Go to next question if answer has been submitted
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      }
      else {
        submitLmsForm(values);

        // Go to results screen if this was the last question
        openStepById('AssessmentResult');
      }
    }
    else {
      // Display hint text if the answer was just submitted
      setIsAnswered(true);
    }
  };

  const answers = questions[currentQuestionIndex].answers;

  // const answers = useMemo(() => questions[currentQuestionIndex].answers.sort(a => {
  //   if (a.text?.toLowerCase() === 'true' || a.text?.toLowerCase() === 'yes') {
  //     return -1;
  //   }
  //   return 0;
  // }), [questions[currentQuestionIndex].answers]);

  // If question has been answered and all the correct answers have been selected
  const isCorrect = isAnswered && checkAnswers(questions[currentQuestionIndex], values.answers[currentQuestionIndex]?.answerIds);

  return (
    <StepContainer
      customHandleBack={customHandleBack}
    >
      <h2>Question {(currentQuestionIndex + 1).toString().padStart(2, '0')}/{questions.length}</h2>

      <ControlInputGroup
        name={questions[currentQuestionIndex].type === AccreditationConstants.SINGLE_ANSWER_TYPE_CRM ? `answers[${currentQuestionIndex}].answerIds` : `answers[${currentQuestionIndex}].answerIds`}
        errorName={`answers[${currentQuestionIndex}].answerIds`}
        label={questions[currentQuestionIndex].text}
        type={questions[currentQuestionIndex].type === AccreditationConstants.SINGLE_ANSWER_TYPE_CRM ? 'radio' : 'checkbox'}
        options={answers.map(answer => {
          return {
            label: answer.text,
            value: answer.id
          };
        })}
        disabled={isAnswered}
      />

      {isAnswered &&
        <AuCallout>
          <h4>{isCorrect ? 'Correct' : 'Incorrect'}</h4>
          <p>{questions[currentQuestionIndex].helpText}</p>
        </AuCallout>
      }

      <FormNavigation
        nextButtonText={isAnswered ? 'Next' : 'Submit answer'}
        onNextAction={handleNext}
        hidePrevious
        showSaveAndExit={false}
        disableNextButton={!isSelection} />
    </StepContainer>
  );
};

export default AssessmentQuestion;
